import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { Link, Navigate, useLocation, useParams, useSearchParams } from "react-router-dom";
import { IconBack, IconForward, IconInfo, IconRetry, IconSolution } from "../../components/Icons";
import { fetchInformation, fetchInformationDetail } from "../../services/information-api";
import { useResource } from "../../state/resource";
import { useSession } from "../../state/session";
import "./information.css";

/** 独立于聊天外壳：不挂载关系请求、角色立绘、支付弹层或音乐控件。 */
export function InformationAccess({ children }: { children: ReactNode }) {
  const session = useSession();
  const location = useLocation();
  if (session.status === "checking") return <div className="app-boot" aria-busy="true" aria-label="正在加载资料查询" />;
  if (session.status === "anonymous" || session.status === "blocked") {
    return <Navigate to="/login" replace state={{ informationReturnTo: location.pathname + location.search }} />;
  }
  return <>{children}</>;
}

function InformationLayout({ children }: { children: ReactNode }) {
  const viewport = useRef<HTMLDivElement>(null);
  const location = useLocation();
  useEffect(() => { viewport.current?.scrollTo(0, 0); }, [location.pathname, location.search]);
  useEffect(() => {
    const previous = document.title;
    document.title = "梦灵缈缈 · 造梦体资料查询";
    return () => { document.title = previous; };
  }, []);
  return (
    <div className="information" ref={viewport}>
      <div className="information__wrap">
        <header className="information__header">
          <Link to="/information" className="information__brand" aria-label="梦灵缈缈资料查询首页">
            <span className="information__brand-mark" aria-hidden="true"><IconSolution size={23} /></span>
            <span>梦灵缈缈<small>造梦体资料库</small></span>
          </Link>
          <span className="information__edition">资料查询</span>
        </header>
        <main>{children}</main>
        <footer className="information__footer">
          <span>梦灵缈缈 · 造梦体资料查询</span>
          <span>关键词检索 · 已发布资料 · 原文阅读</span>
        </footer>
      </div>
    </div>
  );
}

function Notice({ title, children, retry }: { title: string; children: ReactNode; retry?: () => void }) {
  return <div className="information__notice" role="status">
    <IconInfo size={26} /><h3>{title}</h3><p>{children}</p>
    {retry ? <button className="information__text-button" onClick={retry}><IconRetry size={16} />重新加载</button> : null}
  </div>;
}

function Loading() {
  return <div className="information__loading" role="status" aria-label="正在加载资料">
    <span>正在加载资料…</span>
    {[0, 1, 2].map((i) => <div className="information__skeleton" key={i} aria-hidden="true" />)}
  </div>;
}

function publishedDate(date: string | null): string {
  return date ? new Date(date).toLocaleDateString("zh-CN", { timeZone: "Asia/Shanghai", year: "numeric", month: "2-digit", day: "2-digit" }) : "发布日期未标注";
}

/** 只用 React 文本节点高亮，绝不把资料或用户搜索词当 HTML。 */
function Highlight({ text, query }: { text: string; query: string }) {
  const terms = [...new Set(query.toLocaleLowerCase().split(/\s+/u).filter(Boolean))].sort((a, b) => b.length - a.length);
  if (!terms.length) return <>{text}</>;
  const pattern = new RegExp(`(${terms.map((term) => term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})`, "gi");
  return <>{text.split(pattern).map((part, index) => terms.includes(part.toLocaleLowerCase()) ? <mark key={index}>{part}</mark> : part)}</>;
}

export function InformationPage() {
  const [params, setParams] = useSearchParams();
  const q = (params.get("q") ?? "").trim().slice(0, 80);
  const category = params.get("category") ?? "";
  const rawPage = Number(params.get("page"));
  const page = Number.isInteger(rawPage) && rawPage >= 1 && rawPage <= 10000 ? rawPage : 1;
  const [input, setInput] = useState(q);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => setInput(q), [q]);

  function search(next: { q?: string; category?: string; page?: number }) {
    const updated = new URLSearchParams();
    const nextQ = next.q ?? q;
    const nextCategory = next.category ?? category;
    if (nextQ) updated.set("q", nextQ);
    if (nextCategory) updated.set("category", nextCategory);
    if ((next.page ?? 1) > 1) updated.set("page", String(next.page));
    setParams(updated);
  }
  function submit(event: FormEvent) {
    event.preventDefault();
    const nextQ = input.trim();
    if (nextQ === q && page === 1) setAttempt((current) => current + 1);
    else search({ q: nextQ });
  }

  return <InformationLayout>
    <section className="information__search-area" aria-label="搜索资料">
      <form className="information__search" onSubmit={submit} role="search">
        <label className="sr-only" htmlFor="information-search">资料关键词</label>
        <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true"><circle cx="10.5" cy="10.5" r="6.5" /><path d="m16 16 4.5 4.5" /></svg>
        <input id="information-search" type="search" value={input} maxLength={80} onChange={(event) => setInput(event.target.value)} placeholder="输入资料关键词" enterKeyHint="search" autoComplete="off" />
        <button type="submit">查询<IconForward size={15} /></button>
      </form>
    </section>
    <InformationResults key={`${q}:${category}:${page}:${attempt}`} q={q} category={category} page={page} onSearch={search} />
    <aside className="information__about">
      <IconInfo size={18} /><div><h2>关于资料查询</h2><p>本页提供已有资料的检索与阅读，不提供 AI 生成回答或角色对话。查询不会记录为对话，也不消耗灵力。</p></div>
    </aside>
  </InformationLayout>;
}

/** 分类与关键词变化时重新挂载，取消旧请求，不显示上一轮残留结果。 */
function InformationResults({ q, category, page, onSearch }: {
  q: string;
  category: string;
  page: number;
  onSearch: (next: { q?: string; category?: string; page?: number }) => void;
}) {
  const resource = useResource((signal) => fetchInformation({ q, category, page }, signal), [q, category, page]);
  const data = resource.state.phase === "ready" ? resource.state.data : null;
  const resultParams = new URLSearchParams({ ...(q ? { q } : {}), ...(category ? { category } : {}), ...(page > 1 ? { page: String(page) } : {}) });
  return (
    <section className="information__catalogue" aria-labelledby="information-results">
      <div className="information__section-heading">
        <h2 id="information-results">{q ? "查询结果" : "浏览资料"}</h2>
        {data ? <span aria-live="polite">共 {data.total} 篇{q ? "相关资料" : "资料"}</span> : null}
      </div>
      {data && data.categories.length > 0 ? <nav className="information__categories" aria-label="资料分类">
        <button type="button" aria-pressed={!category} onClick={() => onSearch({ category: "" })}>全部资料</button>
        {data.categories.map((item) => <button type="button" key={item.id} aria-pressed={category === item.id} onClick={() => onSearch({ category: item.id })}>{item.name}<span>{item.count}</span></button>)}
      </nav> : null}
      {q ? <div className="information__query-label"><span>关键词：{q}</span><button type="button" onClick={() => onSearch({ q: "" })}>清除关键词</button></div> : null}
      {resource.state.phase === "loading" ? <Loading /> : null}
      {resource.state.phase === "failed" ? <Notice title="暂时无法加载资料" retry={resource.reload}>{resource.state.error.status === 400 ? "请使用 80 字以内、最多 10 个关键词，或清除地址栏中的无效查询条件。" : "资料服务未能连接或请求失败，请稍后重试。此时不会生成或补写查询结果。"}</Notice> : null}
      {data?.items.length === 0 ? <Notice title={q || category ? "未找到相关资料" : "资料正在整理中"}>
        {q || category ? "请尝试其他关键词，或切换到全部资料。" : "当前没有可供查询的已发布资料，请稍后再来查看。"}
        {page > 1 ? <button className="information__text-button" onClick={() => onSearch({ page: 1 })}>返回第一页</button> : null}
      </Notice> : null}
      {data && data.items.length > 0 ? <>
        <div className="information__results">
          {data.items.map((item) => <Link className="information__card" key={item.id} to={`/information/${encodeURIComponent(item.id)}?${resultParams}`}>
            <div className="information__card-meta"><span>{item.categoryName}</span><time dateTime={item.publishedAt ?? undefined}>{publishedDate(item.publishedAt)}</time></div>
            <h3><Highlight text={item.title} query={q} /></h3>
            <p><Highlight text={item.excerpt} query={q} /></p>
            <span className="information__card-link">查看资料原文<IconForward size={16} /></span>
          </Link>)}
        </div>
        {page > 1 || data.hasMore ? <nav className="information__pagination" aria-label="资料翻页">
          <button disabled={page <= 1} onClick={() => onSearch({ page: page - 1 })}><IconBack size={16} />上一页</button>
          <span>第 {page} / {Math.ceil(data.total / data.pageSize)} 页</span>
          <button disabled={!data.hasMore} onClick={() => onSearch({ page: page + 1 })}>下一页<IconForward size={16} /></button>
        </nav> : null}
      </> : null}
    </section>
  );
}

export function InformationDetailPage() {
  const { id = "" } = useParams();
  const location = useLocation();
  const resource = useResource((signal) => fetchInformationDetail(id, signal), [id]);
  const data = resource.state.phase === "ready" ? resource.state.data : null;
  return <InformationLayout>
    <Link className="information__back" to={`/information${location.search}`}><IconBack size={17} />返回资料列表</Link>
    {resource.state.phase === "loading" ? <Loading /> : null}
    {resource.state.phase === "failed" ? <Notice title={resource.state.error.status === 404 ? "资料不存在或已下架" : "暂时无法加载资料"} retry={resource.reload}>请返回列表选择其他资料，或稍后重试。</Notice> : null}
    {data ? <article className="information__article">
      <span className="information__eyebrow">{data.categoryName}</span>
      <h1>{data.title}</h1>
      <div className="information__article-meta"><span>资料原文</span><span>版本 {data.version}</span><time dateTime={data.publishedAt ?? undefined}>发布于 {publishedDate(data.publishedAt)}</time></div>
      {data.summary ? <p className="information__summary">{data.summary}</p> : null}
      <div className="information__body">{data.content}</div>
      <div className="information__source"><IconSolution size={18} /><div>资料来源：{data.categoryName}<small>展示知识库当前已发布版本，未经过 AI 改写。</small></div></div>
    </article> : null}
  </InformationLayout>;
}
